import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight, User, Phone, Briefcase, MapPin, Mail, Gift, Users } from 'lucide-react';

import { API } from '../lib/api';

function setSignupCookie(data) {
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1);
  document.cookie = `gynovate_signed=1; expires=${expires.toUTCString()}; path=/; SameSite=Strict`;
  localStorage.setItem('gynovate_signup', JSON.stringify(data));
}

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

function validateEmail(email) {
  const v = (email || '').trim();
  if (!v) return 'Please enter your email';
  if (!EMAIL_REGEX.test(v)) return 'Please enter a valid email (e.g., you@clinic.com)';
  return null;
}

export default function SignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = (searchParams.get('ref') || '').trim().toUpperCase();
  const [form, setForm] = useState({ name: '', email: '', phone: '', designation: '', place_of_practice: '' });
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inviter, setInviter] = useState(null);
  const [doctorCount, setDoctorCount] = useState(null);

  useEffect(() => {
    if (!refCode) return;
    axios.get(`${API}/api/referral/lookup/${refCode}`)
      .then(r => { if (r.data.valid) setInviter(r.data.inviter); })
      .catch(() => {});
  }, [refCode]);

  useEffect(() => {
    axios.get(`${API}/api/stats/public`)
      .then(r => setDoctorCount(Math.max(Number(r.data.display) || 0, 1300)))
      .catch(() => setDoctorCount(1300));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.designation.trim() || !form.place_of_practice.trim()) {
      toast.error('Please fill all fields');
      return;
    }
    const emailErr = validateEmail(form.email);
    if (emailErr) { toast.error(emailErr); return; }
    const phoneDigits = form.phone.replace(/\D/g, '');
    if (phoneDigits.length < 7) {
      toast.error('Please enter a valid phone number');
      return;
    }
    if (!accepted) {
      toast.error('Please accept the Medical Disclaimer and Privacy Policy to continue');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        email: form.email.trim(),
        accepted_disclaimer: accepted,
        ...(refCode ? { referral_code: refCode } : {}),
      };
      const res = await axios.post(`${API}/api/signup`, payload);
      setSignupCookie(res.data.signup);
      toast.success('Welcome to GYNOVATE AI/SI!');
      navigate('/chat');
    } catch (err) {
      // Surface the real reason — could be Pydantic validation, network, server error
      let msg = 'Signup failed. Please try again.';
      const detail = err?.response?.data?.detail;
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length) {
        // Pydantic v2 returns array of {loc, msg, type}
        const first = detail[0];
        const field = first?.loc?.[first.loc.length - 1] || 'field';
        msg = `${field}: ${first?.msg || 'invalid'}`;
      } else if (err?.message) {
        msg = err.message;
      }
      toast.error(msg, { duration: 6000 });
      // eslint-disable-next-line no-console
      console.error('Signup error:', err?.response?.status, err?.response?.data || err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex relative overflow-hidden"
      style={{
        background:
          'radial-gradient(120% 80% at 0% 0%, #FFF5F6 0%, transparent 55%), radial-gradient(100% 80% at 100% 100%, #FFFAF0 0%, transparent 55%), linear-gradient(180deg, #FAFAF9 0%, #FFF8F4 100%)',
      }}
    >
      {/* ambient glows */}
      <div className="pointer-events-none absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full opacity-40 blur-3xl" style={{ background: 'radial-gradient(circle, #FFE6D5 0%, transparent 70%)' }} />
      <div className="pointer-events-none absolute -bottom-40 -right-32 w-[480px] h-[480px] rounded-full opacity-30 blur-3xl" style={{ background: 'radial-gradient(circle, #F3E8FF 0%, transparent 70%)' }} />

      {/* Left panel – branding (editorial, light) */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 relative z-10">
        <div>
          <div className="flex items-center gap-3 mb-16">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-raspberry-700 to-[#7a0e2e] flex items-center justify-center shadow-md shadow-raspberry-200">
              <span className="text-white font-black text-lg">G</span>
            </div>
            <span className="text-slate-900 font-bold text-lg tracking-tight">Gynovate AI/SI<sup className="text-xs text-raspberry-700">™</sup></span>
          </div>
          <h2 className="text-5xl font-black text-slate-900 leading-[1.05] mb-5 tracking-tighter">
            The AI/SI Copilot<br />Every Doctor<br /><span className="italic font-light text-raspberry-700" style={{ fontFamily: 'Fraunces, serif' }}>Deserves.</span>
          </h2>
          <p className="text-slate-600 text-base leading-relaxed max-w-sm">
            Evidence-based clinical answers, drug protocols, OT notes, and patient communications — all in seconds.
          </p>
        </div>
        <div className="space-y-2.5">
          {['51+ specialised clinical modes', 'Evidence-based answers in seconds', 'FOGSI / WHO / ACOG guidelines', 'WhatsApp sharing for patients', 'Indian formulary & MTP Act context'].map(f => (
            <div key={f} className="flex items-center gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-raspberry-700 flex-shrink-0" />
              <span className="text-slate-700 text-sm">{f}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Right panel – form (glass-morphic) */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 relative z-10">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-raspberry-700 to-[#7a0e2e] flex items-center justify-center">
              <span className="text-white font-black">G</span>
            </div>
            <span className="text-slate-900 font-bold tracking-tight">Gynovate AI/SI<sup className="text-xs text-raspberry-700">™</sup></span>
          </div>

          <div className="bg-white/70 backdrop-blur-xl border border-white/60 rounded-3xl p-8 shadow-[0_8px_40px_rgb(192,18,60,0.08)]">
            <div className="mb-6">
              <h2 className="text-3xl font-black text-slate-900 tracking-tight mb-1">Free Access</h2>
              <p className="text-slate-500 text-sm">No credit card required</p>
            </div>

            {inviter && (
              <div
                className="mb-4 flex items-start gap-3 p-3.5 rounded-2xl border bg-raspberry-50 border-raspberry-200"
                data-testid="inviter-banner"
              >
                <Gift className="w-5 h-5 text-raspberry-700 flex-shrink-0 mt-0.5" />
                <div className="text-slate-800 text-sm leading-snug">
                  <div className="font-bold">Invited by {inviter.name}</div>
                  <div className="text-slate-500 text-xs mt-0.5">
                    {inviter.designation} · {inviter.place_of_practice}
                  </div>
                </div>
              </div>
            )}

            {doctorCount && (
              <div
                className="mb-5 flex items-center gap-3 p-3.5 rounded-2xl border bg-white/80 border-slate-200"
                data-testid="social-proof-banner"
              >
                <div className="flex -space-x-2 flex-shrink-0">
                  {['#C0123C', '#F59E0B', '#7C3AED'].map((c, i) => (
                    <div
                      key={i}
                      className="w-7 h-7 rounded-full border-2 border-white flex items-center justify-center text-white text-[10px] font-black"
                      style={{ background: c }}
                    >
                      {['PS', 'RK', 'VD'][i]}
                    </div>
                  ))}
                </div>
                <div className="text-slate-800 text-xs leading-snug flex-1">
                  <div className="font-bold flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5 text-raspberry-700" />
                    {doctorCount.toLocaleString('en-IN')}+ doctors
                  </div>
                  <div className="text-slate-500 mt-0.5">across India already trust Gynovate AI/SI</div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3" data-testid="signup-form">
              {/* Name */}
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="Full Name (e.g. Dr. Priya Sharma)"
                  className="w-full pl-10 pr-4 py-3 text-sm rounded-xl border bg-white/80 border-slate-200 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-raspberry-300 focus:border-raspberry-400 transition-all"
                  data-testid="signup-name"
                />
              </div>

              {/* Email */}
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="Email Address"
                  className="w-full pl-10 pr-4 py-3 text-sm rounded-xl border bg-white/80 border-slate-200 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-raspberry-300 focus:border-raspberry-400 transition-all"
                  data-testid="signup-email"
                />
              </div>

              {/* Phone */}
              <div className="relative">
                <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="tel"
                  value={form.phone}
                  onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                  placeholder="Phone Number"
                  className="w-full pl-10 pr-4 py-3 text-sm rounded-xl border bg-white/80 border-slate-200 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-raspberry-300 focus:border-raspberry-400 transition-all"
                  data-testid="signup-phone"
                />
              </div>

              {/* Designation */}
              <div className="relative">
                <Briefcase className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10 pointer-events-none" />
                <select
                  value={form.designation}
                  onChange={e => setForm(p => ({ ...p, designation: e.target.value }))}
                  className="w-full pl-10 pr-4 py-3 text-sm rounded-xl border bg-white/80 border-slate-200 text-slate-900 focus:outline-none focus:ring-2 focus:ring-raspberry-300 focus:border-raspberry-400 transition-all appearance-none"
                  data-testid="signup-designation"
                >
                  <option value="">Select Designation</option>
                  <option value="MBBS">MBBS</option>
                  <option value="MD">MD</option>
                  <option value="MS">MS</option>
                  <option value="DNB">DNB</option>
                  <option value="DM">DM (Super-speciality)</option>
                  <option value="MCh">MCh (Super-speciality)</option>
                  <option value="DGO">DGO</option>
                  <option value="Diploma">Diploma (Any speciality)</option>
                  <option value="Fellowship">Fellowship</option>
                  <option value="FRCOG">FRCOG</option>
                  <option value="FICOG">FICOG</option>
                  <option value="PG Resident">PG Resident</option>
                  <option value="Medical Student">Medical Student</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Place */}
              <div className="relative">
                <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={form.place_of_practice}
                  onChange={e => setForm(p => ({ ...p, place_of_practice: e.target.value }))}
                  placeholder="Place of Practice (e.g. City Hospital, Mumbai)"
                  className="w-full pl-10 pr-4 py-3 text-sm rounded-xl border bg-white/80 border-slate-200 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-raspberry-300 focus:border-raspberry-400 transition-all"
                  data-testid="signup-place"
                />
              </div>

              {/* Disclaimer + Privacy acceptance checkbox — required */}
              <label
                className={`flex items-start gap-3 rounded-2xl border p-3 cursor-pointer transition-colors select-none mt-2 ${
                  accepted ? 'bg-amber-50 border-amber-300' : 'bg-white/60 border-slate-200'
                }`}
                data-testid="signup-disclaimer-wrapper"
              >
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  className="mt-0.5 w-4 h-4 flex-shrink-0 accent-raspberry-700 cursor-pointer"
                  data-testid="signup-disclaimer-checkbox"
                />
                <span className="text-[11.5px] leading-snug text-slate-700">
                  I confirm I am a registered medical practitioner and I have read, understood, and agreed to the{' '}
                  <RouterLink to="/disclaimer" target="_blank" className="text-raspberry-700 font-semibold hover:underline" data-testid="signup-disclaimer-link">
                    Medical Disclaimer
                  </RouterLink>{' '}
                  and{' '}
                  <RouterLink to="/privacy" target="_blank" className="text-raspberry-700 font-semibold hover:underline" data-testid="signup-privacy-link">
                    Privacy Policy
                  </RouterLink>
                  . I accept full clinical responsibility for all decisions made using Gynovate AI/SI.
                </span>
              </label>

              <button
                type="submit"
                disabled={loading || !accepted}
                className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-raspberry-700 to-[#7a0e2e] text-white text-sm font-bold hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 mt-2 shadow-lg shadow-raspberry-200"
                data-testid="signup-submit"
              >
                {loading ? 'Signing up...' : 'Get Free Access'}
                {!loading && <ArrowRight className="w-4 h-4" />}
              </button>
            </form>

            <p className="text-xs text-slate-400 text-center mt-5">
              Questions? Read the{' '}
              <RouterLink to="/disclaimer" className="text-slate-600 underline">Medical Disclaimer</RouterLink> and{' '}
              <RouterLink to="/privacy" className="text-slate-600 underline">Privacy Policy</RouterLink>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
